import { inject, injectable } from 'inversify';
import { Observable, ReplaySubject, catchError, filter, finalize, map, switchMap, tap, throwError } from 'rxjs';
import { AjaxConfig, AjaxError, AjaxResponse, ajax } from 'rxjs/ajax';

import { HttpRequestBody, HttpRequestOptions, HttpRequestOptionsInternal } from '../types/http.types';
import { get, parseInt } from '../utils/lodash-min';
import { setClockOffsetFromDateHeader } from '../utils/clock.util';
import { isAccessTokenStale, isAnonymousToken } from '../utils/token-expiry.util';
import { ClientError, ServiceError, UnknownHTTPError } from '../utils/request.utils';
import { ApiConfig } from './api-config';
import { DOMStorage } from './dom-storage.service';
import { EnvironmentService } from './environment.service';
// Type-only import: RefreshService imports LightHttp, so a runtime import here
// would create a cycle. We only need the type for the static provider.
import type { RefreshService } from './refresh.service';
// Type-only import for the same reason (StepUpService imports LightHttp).
import type { StepUpService } from './step-up.service';
import { ILightHttpService } from './service-interfaces';

export const UNAUTHENTICATED_URLS = [
    new RegExp('^https://storage.googleapis.com.*', 'i'),
    new RegExp('^https://checkip.amazonaws.com.*', 'i'),
    new RegExp('.*style.*', 'i'),
];


export type HttpProgressEventType = 'upload_loadstart' |
    'upload_load' |
    'upload_progress' |
    'download_loadstart' |
    'download_load' |
    'download_progress';
export class HttpProgressEventTypes {
    public static UPLOAD_LOADSTART: HttpProgressEventType = 'upload_loadstart';
    public static UPLOAD_LOAD: HttpProgressEventType = 'upload_load';
    public static UPLOAD_PROGRESS: HttpProgressEventType = 'upload_progress';
    public static DOWNLOAD_LOADSTART: HttpProgressEventType = 'download_loadstart';
    public static DOWNLOAD_LOAD: HttpProgressEventType = 'download_load';
    public static DOWNLOAD_PROGRESS: HttpProgressEventType = 'download_progress';
}

export interface HttpProgressEvent {
    type: HttpProgressEventType;
    event: {
        total: number;
        loaded: number;
    };
}

export interface HttpResponseEvent<T> {
    response$: Observable<T>;
    events$: Observable<HttpProgressEvent>;
}

@injectable()
export class LightHttp implements ILightHttpService {

    private static wsDataProvider = {
        getOrCreateStoredWSId: (): string | null => null,
        getStoredWSDomain: (): string | null => null,
    };
    static loginUserDetailsProvider: { userIpAddress: string | null, userCountry: string | null } = { userIpAddress: null, userCountry: null };
    static commonHttpHeadersProvider: { getCommonHttpHeaders: () => any }
        = { getCommonHttpHeaders: () => { return {}; } };
    static authTokenProvider: { getToken: () => string | null } = { getToken: () => null };

    /**
     * Refresh-before-send wiring. DEFAULT OFF: when refreshEnabled is false the
     * guard at the top of request() is a complete no-op and request() behaves
     * EXACTLY as before this feature existed. Both are wired at app bootstrap
     * (settings.featureFlags -> setRefreshEnabled; registerRefreshProvider).
     */
    static refreshEnabled = false;
    static refreshProvider: RefreshService | null = null;

    /**
     * Step-up (re-authentication) wiring. Part of the same refresh-token
     * feature, so it ALSO respects refreshEnabled: when the flag is off the
     * step-up branch is a complete no-op. Wired at bootstrap alongside
     * refreshProvider.
     */
    static stepUpProvider: StepUpService | null = null;

    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(EnvironmentService) private env: EnvironmentService,
        @inject(DOMStorage) private domStorage: DOMStorage,
    ) {
    }

    public post<T>(
        url: string,
        body: HttpRequestBody,
        options?: HttpRequestOptions,
    ) {
        return this.request<T>(url, 'POST', body, options).response$;
    }

    public put<T>(
        url: string,
        body: HttpRequestBody,
        options?: HttpRequestOptions,
    ) {
        return this.request<T>(url, 'PUT', body, options).response$;
    }

    public patch<T>(
        url: string,
        body: HttpRequestBody,
        options?: HttpRequestOptions,
    ) {
        return this.request<T>(url, 'PATCH', body, options).response$;
    }

    public get<T>(
        url: string,
        options?: HttpRequestOptions,
    ) {
        return this.request<T>(url, 'GET', null, options).response$;
    }

    public delete<T>(
        url: string,
        options?: HttpRequestOptions,
    ) {
        return this.request<T>(url, 'DELETE', null, options).response$;
    }

    /**
     * Default content type to json,
     * requestGeneratorFn allow retry() on rxjs
     * @param url 
     * @param method 
     * @param body 
     * @param options 
     */
    public request<T>(
        url: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD' | 'PATCH',
        body: HttpRequestBody | null,
        options: HttpRequestOptions = {},
    ): HttpResponseEvent<T> {
        // Refresh-if-stale guard (covers ironman's silent anon-degrade, §6.3).
        // getCommonHeaders() is synchronous but refresh is async, so when ALL
        // of the conditions below hold we convert this into "refresh first, then
        // send" via refreshThenRequest(). When refreshEnabled is false (the
        // default + deployed state) this short-circuits and nothing else runs.
        if (
            LightHttp.refreshEnabled &&
            LightHttp.refreshProvider != null &&
            !LightHttp.refreshProvider.cookieSessionUnavailable &&
            !options?.__isAuthRetry &&
            !this.isAuthEndpoint(url) &&
            !UNAUTHENTICATED_URLS.find((r) => r.test(url))
        ) {
            const token = LightHttp.authTokenProvider.getToken();
            if (token && !isAnonymousToken(token) && isAccessTokenStale(token)) {
                return this.refreshThenRequest<T>(url, method, body, options);
            }
        }

        this.mergeUserRequestedHeadersWithDefaultHeaders(url, options);
        // Capture whatever Accept header this request ends up carrying
        // (explicit, caller-supplied — LightHttp's own default headers never
        // set one) so a failure can report it (406 Accept-header diagnosis).
        const requestAcceptHeader = this.resolveAcceptHeader(options.headers);
        const config: AjaxConfig = {
            url: url,
            method: method,
            includeDownloadProgress: true,
            includeUploadProgress: true,
            body: body,
            ...(options.params && { queryParams: options.params }),
            ...(options.headers && { headers: options.headers }),
            ...(options.withCredentials && { withCredentials: options.withCredentials }),
            ...(options.responseType && { responseType: options.responseType }),
            ...(options.timeout && { timeout: options.timeout }),
        };
        const eventsListner = new ReplaySubject<HttpProgressEvent>(1);
        const requestStartTime = Date.now();
        // const requestEvents = new ReplaySubject<any>(1);
        const requestEvents = ajax<T>(config)
            .pipe(
                tap((data) => {
                    if (data.type != 'download_load') {
                        eventsListner.next(this.convertToProgressEvent(data));
                    } else {
                        // Parse `Date` from getAllResponseHeaders() rather than
                        // getResponseHeader('Date'): the latter logs a noisy
                        // "Refused to get unsafe header" console error on every
                        // cross-origin API response (Date is not CORS-safelisted,
                        // and our API is always a different subdomain). getAll…()
                        // never logs — it simply omits non-exposed headers, so the
                        // clock-skew offset stays 0 (today's behaviour) until/unless
                        // professorx exposes Date via Access-Control-Expose-Headers.
                        const allHeaders = data.xhr?.getAllResponseHeaders?.() || '';
                        const dateMatch = /^date:\s*(.+)$/im.exec(allHeaders);
                        setClockOffsetFromDateHeader(dateMatch ? dateMatch[1].trim() : null);
                    }
                }),
                filter((data) => {
                    return data.type == 'download_load'
                }),
                map(data => data.response),
                // catchError runs AFTER filter/map so the success path is fully
                // unwrapped to T first. On error this converts the AjaxError and
                // then either replays (reactive refresh, yielding an already
                // unwrapped T that flows straight to the subscriber) or rethrows.
                catchError(error => {
                    const wrapped = this.convertErrorResponse(error, { requestUrl: url, elapsedMs: Date.now() - requestStartTime, requestAcceptHeader });
                    // Reactive refresh-and-replay backstop (R2-B3/M8): when an
                    // authenticated request comes back 401 because the access
                    // token went stale (marker error="invalid_token", or a bare
                    // 401 fallback), refresh ONCE and replay the request with a
                    // fresh bearer. Gated on refreshEnabled FIRST, so when the
                    // flag is off this is a complete no-op and the original error
                    // propagates exactly as before.
                    const replay$ = this.maybeReactiveRefreshReplay<T>(url, method, body, options, wrapped);
                    if (replay$) {
                        return replay$;
                    }
                    // Step-up-and-replay (distinct from silent refresh): a
                    // step_up_required 401 means the user must re-prove their
                    // password before this action proceeds. Prompt → /auth/reauth
                    // → replay ONCE. Gated on refreshEnabled too (same feature).
                    const stepUp$ = this.maybeStepUpReplay<T>(url, method, body, options, wrapped);
                    if (stepUp$) {
                        return stepUp$;
                    }
                    return throwError(() => wrapped);
                }),
                finalize(() => eventsListner.complete()),
            )
        /*
        .subscribe({
            next: (data) => {
                if (data.type == 'download_load') {
                    requestEvents.next(data.response);
                    requestEvents.complete();
                } else {
                    eventsListner.next(this.convertToProgressEvent(data));
                }
            },
            error: (error) => {
                requestEvents.error(this.convertErrorResponse(error, { requestUrl: url }));
            }
        });
        */
        return {
            response$: requestEvents,
            events$: eventsListner.asObservable(),
        };
    }

    /** True when the URL targets the auth controller, so auth flows (refresh,
     * login, etc.) never trigger refresh-before-send. */
    private isAuthEndpoint(url: string): boolean {
        return url.indexOf('/v1/api/auth/') !== -1;
    }

    /**
     * True for the /user-details endpoint. The login.service self-heal (L2)
     * OWNS the 401 path there (drops the bearer, emits registered:false), so
     * the reactive refresh-and-replay backstop must EXCLUDE it to avoid
     * colliding with that flow. (Note: /user-details is also under /v1/api/auth/
     * so isAuthEndpoint already covers it; this is an explicit, intent-revealing
     * second guard.)
     */
    private isUserDetailsEndpoint(url: string): boolean {
        return url.indexOf('/v1/api/auth/user-details') !== -1;
    }

    /**
     * Reactive refresh-and-replay decision (Task 20, R2-B3/M8). Returns an
     * Observable<T> that refreshes ONCE and replays the request with a fresh
     * bearer when ALL gates pass; returns null otherwise (the caller then
     * propagates the original error).
     *
     * Gates (in order — refreshEnabled FIRST so flag-off is a total no-op):
     *   - LightHttp.refreshEnabled
     *   - refreshProvider non-null and !cookieSessionUnavailable
     *   - NOT already an auth retry (__isAuthRetry) — guarantees a SINGLE replay;
     *     a second 401 on the replay propagates (NO loop)
     *   - NOT an auth endpoint, NOT /user-details (self-heal owns that)
     *   - NOT an UNAUTHENTICATED_URL
     *   - stored token present and non-anonymous
     *   - the 401 signal: err.authError === 'invalid_token' (primary marker), OR
     *     a bare status === 401 (fallback for proxies/edge that strip the header)
     *
     * The replay sets __isAuthRetry and strips any caller-supplied stale
     * Authorization header so request()'s getCommonHeaders() re-injects the
     * FRESH bearer (same approach as refreshThenRequest, Task 19b). If refresh()
     * itself fails (it calls clearLocalSession on a definitive 401), that error
     * propagates.
     */
    private maybeReactiveRefreshReplay<T>(
        url: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD' | 'PATCH',
        body: HttpRequestBody | null,
        options: HttpRequestOptions,
        wrapped: Error,
    ): Observable<T> | null {
        // Flag-off: total no-op (gate FIRST). Also bail when the wiring is not
        // present, the request is already a retry, or it targets an auth/
        // user-details/unauthenticated URL.
        if (
            !LightHttp.refreshEnabled ||
            LightHttp.refreshProvider == null ||
            LightHttp.refreshProvider.cookieSessionUnavailable ||
            options?.__isAuthRetry ||
            this.isAuthEndpoint(url) ||
            this.isUserDetailsEndpoint(url) ||
            UNAUTHENTICATED_URLS.find((r) => r.test(url))
        ) {
            return null;
        }

        // The 401 signal: prefer the explicit marker; fall back to a bare 401.
        const status = (wrapped as ClientError)?.status;
        const authError = (wrapped as ClientError)?.authError;
        const isInvalidTokenMarker = authError === 'invalid_token';
        const isBare401 = authError == null && status === 401;
        if (!isInvalidTokenMarker && !isBare401) {
            return null;
        }

        // Only refresh for a real (non-anonymous) session.
        const token = LightHttp.authTokenProvider.getToken();
        if (!token || isAnonymousToken(token)) {
            return null;
        }

        // Strip a stale Authorization header from carried options so the replay
        // picks up the FRESH bearer (clone — never mutate the caller's objects).
        const retryOptions: HttpRequestOptions = { ...options, __isAuthRetry: true };
        if (retryOptions.headers && (retryOptions.headers.Authorization || retryOptions.headers.authorization)) {
            const clonedHeaders = { ...retryOptions.headers };
            delete clonedHeaders.Authorization;
            delete clonedHeaders.authorization;
            retryOptions.headers = clonedHeaders;
        }

        return LightHttp.refreshProvider.refresh().pipe(
            switchMap(() => this.request<T>(url, method, body, retryOptions).response$),
        );
    }

    /**
     * Reactive STEP-UP-and-replay decision (Task 20b). Returns an Observable<T>
     * that prompts for the password, re-authenticates against /auth/reauth, and
     * replays the request ONCE when ALL gates pass; null otherwise (caller
     * propagates the original error).
     *
     * Distinct from maybeReactiveRefreshReplay():
     *   - fires ONLY on the explicit `step_up_required` marker (never a bare 401)
     *   - is INTERACTIVE (prompts the user) — never silent
     *   - applies to authed NON-auth endpoints INCLUDING /me/billing/... ; it
     *     does NOT exclude /user-details specially (the self-heal there owns
     *     invalid_token, not step_up_required)
     *
     * Shared gates: refreshEnabled FIRST (flag-off = total no-op), provider
     * wired, NOT already a retry (single replay — a second step_up on the replay
     * propagates, NO loop), NOT an auth endpoint (so the /auth/reauth POST itself
     * can never recurse), NOT an UNAUTHENTICATED_URL, and a present non-anonymous
     * token. The replay sets __isAuthRetry and strips any stale Authorization
     * header so the ELEVATED bearer is re-injected.
     */
    private maybeStepUpReplay<T>(
        url: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD' | 'PATCH',
        body: HttpRequestBody | null,
        options: HttpRequestOptions,
        wrapped: Error,
    ): Observable<T> | null {
        if (
            !LightHttp.refreshEnabled ||
            LightHttp.stepUpProvider == null ||
            options?.__isAuthRetry ||
            this.isAuthEndpoint(url) ||
            UNAUTHENTICATED_URLS.find((r) => r.test(url))
        ) {
            return null;
        }

        // Only the explicit step_up_required marker triggers this branch.
        const authError = (wrapped as ClientError)?.authError;
        if (authError !== 'step_up_required') {
            return null;
        }

        // Only for a real (non-anonymous) session.
        const token = LightHttp.authTokenProvider.getToken();
        if (!token || isAnonymousToken(token)) {
            return null;
        }

        // Strip a stale Authorization header so the replay picks up the ELEVATED
        // bearer (clone — never mutate the caller's objects).
        const retryOptions: HttpRequestOptions = { ...options, __isAuthRetry: true };
        if (retryOptions.headers && (retryOptions.headers.Authorization || retryOptions.headers.authorization)) {
            const clonedHeaders = { ...retryOptions.headers };
            delete clonedHeaders.Authorization;
            delete clonedHeaders.authorization;
            retryOptions.headers = clonedHeaders;
        }

        return LightHttp.stepUpProvider.elevate().pipe(
            switchMap(() => this.request<T>(url, method, body, retryOptions).response$),
        );
    }

    /**
     * Runs refresh() first, then performs the real request with a fresh bearer.
     * Returns the SAME { response$, events$ } contract. The inner request is
     * built lazily (after refresh resolves) so it picks up the new token, and is
     * flagged __isAuthRetry to prevent re-entering the guard (no recursion).
     *
     * events$ bridging (Review M7): the caller subscribes to events$ separately
     * and synchronously right after request() returns, so it must not miss early
     * progress events emitted during the refresh round-trip. We hand back a
     * ReplaySubject up front and, once refresh completes and the inner request
     * exists, forward the inner events$ (next/error/complete) into it.
     */
    private refreshThenRequest<T>(
        url: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD' | 'PATCH',
        body: HttpRequestBody | null,
        options: HttpRequestOptions,
    ): HttpResponseEvent<T> {
        // bufferSize 1: the caller subscribes synchronously (before the refresh
        // round-trip resolves), so replaying the latest progress event is enough —
        // an unbounded buffer would retain every event for the subject's lifetime.
        const eventsBridge = new ReplaySubject<HttpProgressEvent>(1);
        let inner: HttpResponseEvent<T> | null = null;

        // Strip any caller-supplied Authorization header before the retry so the
        // inner request()'s getCommonHeaders() re-injects the FRESH bearer rather
        // than reusing the pre-refresh (stale) one. Clone — never mutate the
        // caller's options/headers objects.
        const retryOptions: HttpRequestOptions = { ...options, __isAuthRetry: true };
        if (retryOptions.headers && (retryOptions.headers.Authorization || retryOptions.headers.authorization)) {
            const clonedHeaders = { ...retryOptions.headers };
            delete clonedHeaders.Authorization;
            delete clonedHeaders.authorization;
            retryOptions.headers = clonedHeaders;
        }

        const buildInner = (): HttpResponseEvent<T> => {
            if (!inner) {
                inner = this.request<T>(url, method, body, retryOptions);
                inner.events$.subscribe({
                    next: (e) => eventsBridge.next(e),
                    error: (err) => eventsBridge.error(err),
                    complete: () => eventsBridge.complete(),
                });
            }
            return inner;
        };

        const response$ = LightHttp.refreshProvider!.refreshAcrossTabs().pipe(
            switchMap(() => buildInner().response$),
        );

        return {
            response$,
            events$: eventsBridge.asObservable(),
        };
    }

    private convertToProgressEvent(event: AjaxResponse<any>): HttpProgressEvent {
        if (event.type === 'upload_progress' || event.type === 'download_progress') {
            return {
                event: {
                    loaded: event.loaded,
                    total: event.total,
                },
                type: event.type === 'upload_progress' ? 'upload_progress' : 'download_progress',
            }
        }

        return {
            event: {
                loaded: 0,
                total: 0,
            },
            type: event.type,
        };
    }

    private convertErrorResponse(error: AjaxError, options?: { requestUrl: string, elapsedMs?: number, requestAcceptHeader?: string }) {
        const elapsed = options?.elapsedMs != null ? ` (${options.elapsedMs}ms)` : '';
        if (error.status == 0) {
            let url = get(error || {}, 'xhr.responseURL', options?.requestUrl);
            if ((!url || url.length == 0) && options?.requestUrl) {
                url = options?.requestUrl;
            }
            return new UnknownHTTPError(`Unable to reach the server. Possible causes could be server is offline, your connection to the server is blocked or you are offline. ${url}${elapsed}`, options?.requestAcceptHeader);
        }

        const status = parseInt(error.status / 100 + '');
        if (status === 4) {
            // R2-B3 capture: the raw AjaxError.xhr (with its WWW-Authenticate
            // header) is DISCARDED by the wrapped error, so read the marker here
            // — the only place it is still in scope — and thread it onto the
            // ClientError so the reactive refresh-and-replay backstop can read
            // err.authError downstream. Best-effort: ?.+try can never throw.
            return new ClientError(this.getErrorMessage(error, options) + elapsed, error.response, error.status, this.parseAuthErrorMarker(error), options?.requestAcceptHeader);
        } else if (status === 5) {
            return new ServiceError(this.getErrorMessage(error, options) + elapsed, error.status, options?.requestAcceptHeader);
        } else {
            return new UnknownHTTPError(this.getErrorMessage(error, options) + elapsed, options?.requestAcceptHeader);
        }
    }

    /**
     * Resolves the Accept header value this request actually carries — the
     * client code's explicit choice (LightHttp's own default headers never
     * set one; see getCommonHeaders()) — for inclusion in failed-request
     * error reports. Returns `<none>` when no explicit Accept was set.
     */
    private resolveAcceptHeader(headers?: { [key: string]: any }): string {
        const value = headers?.Accept ?? headers?.accept;
        return value != null && value !== '' ? String(value) : '<none>';
    }

    /**
     * R2-B3: parse the `error="..."` token out of the response's
     * `WWW-Authenticate` header (RFC 6750 Bearer challenge), e.g.
     *   `Bearer error="invalid_token", error_description="..."`
     * Returns `invalid_token`, `step_up_required`, etc., or undefined when the
     * header is absent/unparseable. Strictly best-effort — every access is
     * guarded so this can NEVER throw inside the ajax error path.
     */
    private parseAuthErrorMarker(error: AjaxError): string | undefined {
        try {
            const header = error?.xhr?.getResponseHeader?.('WWW-Authenticate');
            if (!header) {
                return undefined;
            }
            const match = /error="([^"]+)"/i.exec(header);
            return match ? match[1] : undefined;
        } catch {
            return undefined;
        }
    }

    private getErrorMessage(error: AjaxError, options?: { requestUrl?: string }): string {
        let message = get(error, 'response.message') || get(error, 'response.error');
        const url = get(error, 'xhr.responseURL');
        if (message) {
            return message;
        }
        if (error.status || error.status === 0) {
            if (error.response) {
                return `${error.status} - ${typeof error.response === 'object' ? JSON.stringify(error.response) : error.response}`;
            }
            if (url) {
                return `${error.status} - unknow error while connecting to ${url}`;
            }
            return `${error.status} - unknow error`;
        }
        var resolvedUrl = url || options?.requestUrl;
        if (resolvedUrl) {
            return `Unknow error while connecting to ${resolvedUrl}`;
        }
        return `Unknow error`;
    }

    private mergeUserRequestedHeadersWithDefaultHeaders(url: string, options: HttpRequestOptionsInternal) {
        if (!options.headers) {
            options.headers = {};
        }
        const commonHeaders = this.getCommonHeaders(url)
        let allHeaders = Object.assign({}, commonHeaders, options.headers);
        options.headers = allHeaders;
    }

    public getCommonHeaders(url: string) {
        // NOTE: LightHttp is intended ONLY for internal xconvert service endpoints
        // (professorx, ironman, falcon, pod). It unconditionally attaches the user's
        // Authorization bearer (and other identifying headers) to every request unless
        // the URL matches UNAUTHENTICATED_URLS. DO NOT use LightHttp for third-party
        // or untrusted endpoints — the bearer would be leaked. For third-party calls
        // use raw fetch/ajax or a separate client.
        let headers: any = {};
        const token = LightHttp.authTokenProvider.getToken();
        if (!UNAUTHENTICATED_URLS.find(regex => regex.test(url))) {
            headers = LightHttp.commonHttpHeadersProvider.getCommonHttpHeaders();
            // IMPORTANT: Need to add GCP, blob://, sockjs-node to skip url list
            headers = Object.assign(
                {},
                headers,
                {
                    /** API Domain name.
                     * //TODO please rename sitename to apiEndpointDomain
                     */
                    sitename: `${this.apiConfig.apiDomainName}`,
                    /** Domain name of the requesting website  */
                    domainname: `${this.env?.domainname ?? (typeof window !== 'undefined' ? window.location.host : '')}`,
                    ...(LightHttp.loginUserDetailsProvider.userIpAddress ? { 'ip-address': LightHttp.loginUserDetailsProvider.userIpAddress } : null),
                    ...(LightHttp.loginUserDetailsProvider.userCountry ? { 'country': LightHttp.loginUserDetailsProvider.userCountry } : null),
                    wsId: `${LightHttp.wsDataProvider.getOrCreateStoredWSId()}`,
                    wsDomain: `${LightHttp.wsDataProvider.getStoredWSDomain()}`,
                }
            );

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }
        return headers;
    }

    public static registerWSDetailProvider(provider: {
        getOrCreateStoredWSId: () => string | null,
        getStoredWSDomain: () => string | null,
    }) {
        LightHttp.wsDataProvider = provider;
    }

    public static registerLoginUserDetailsProvider(provider: { userIpAddress: string, userCountry: string }) {
        LightHttp.loginUserDetailsProvider = provider;
    }

    public static registerCommonHttpHeadersProvider(provider: { getCommonHttpHeaders: () => any }) {
        LightHttp.commonHttpHeadersProvider = provider;
    }

    public static registerAuthTokenProvider(provider: { getToken: () => string }) {
        LightHttp.authTokenProvider = provider;
    }

    public static registerRefreshProvider(provider: RefreshService) {
        LightHttp.refreshProvider = provider;
    }

    public static registerStepUpProvider(provider: StepUpService) {
        LightHttp.stepUpProvider = provider;
    }

    public static setRefreshEnabled(enabled: boolean) {
        LightHttp.refreshEnabled = enabled;
    }
}
